import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, setToken, getToken } from "./api";
import {
  hydrateKeysOnLogin,
  provisionKeypairAtSignup,
  getLocalPrivateJwk,
  clearLocalPrivateJwk,
} from "./keys";

const AuthCtx = createContext(null);

async function hydrateAtBoot(user, setUser) {
  if (!user) return;
  // On boot we don't have the password. If local key exists, we're set.
  // Otherwise leave keys empty — encryption will hydrate on next sign-in.
  const local = getLocalPrivateJwk(user.id);
  if (!local) return;
  if (!user.public_key_jwk) {
    // Push the local public key up so server knows about it.
    try {
      // We can derive the public from the private JWK fields (RSA JWK includes n/e public params).
      const publicJwk = {
        kty: local.kty,
        n: local.n,
        e: local.e,
        alg: local.alg,
        ext: true,
        key_ops: ["encrypt"],
      };
      const r = await api.patch("/users/me/key", { public_key_jwk: publicJwk });
      setUser(r.data);
    } catch (_e) {
      // ignore
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (res) => {
        setUser(res.data);
        await hydrateAtBoot(res.data, setUser);
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const signIn = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setToken(res.data.token);
    setUser(res.data.user);
    try {
      const result = await hydrateKeysOnLogin(res.data.user, password);
      if (result?.freshlyGenerated || result?.recovered) {
        // Refresh user object to pick up newly stored public/wrapped keys
        const me = await api.get("/auth/me");
        setUser(me.data);
      }
    } catch (_e) {
      // crypto failure — still allow sign-in
    }
    return res.data.user;
  };

  const signUp = async (payload) => {
    let publicKeyJwk = null;
    let wrappedPrivateKey = null;
    try {
      const provisioned = await provisionKeypairAtSignup(payload.password);
      publicKeyJwk = provisioned.publicJwk;
      wrappedPrivateKey = provisioned.wrappedPrivateKey;
    } catch (_e) {
      // crypto unavailable — proceed without keys
    }
    const enriched = {
      ...payload,
      timezone_offset_minutes: -new Date().getTimezoneOffset(),
      public_key_jwk: publicKeyJwk,
      wrapped_private_key: wrappedPrivateKey,
    };
    const res = await api.post("/auth/register", enriched);
    setToken(res.data.token);
    setUser(res.data.user);
    // Store the private key locally
    if (publicKeyJwk) {
      const { provisionKeypairAtSignup: _p } = await import("./keys");
      // We already generated it; pull from the provisioned object isn't available outside.
      // Easier: call hydrateKeysOnLogin which will find no local key, try to unwrap (which works
      // because we just uploaded the wrap), and store locally.
      await hydrateKeysOnLogin(res.data.user, payload.password);
    }
    return res.data.user;
  };

  const signOut = () => {
    setToken(null);
    setUser(null);
  };

  const forgetMe = async () => {
    const r = await api.delete("/users/me");
    if (user) clearLocalPrivateJwk(user.id);
    setToken(null);
    setUser(null);
    return r.data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signUp, signOut, forgetMe, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6A6863] font-serif italic text-xl">
        a quiet moment…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/signin" replace state={{ from: loc.pathname }} />;
  }
  return children;
}
